// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductBuyButton from "@awc/App/Components/Product/ProductBuyButton";
import ProductGridContainer, { ProductGridContainerStyle } from "@awc/App/Components/Product/ProductGridContainer";
import ProductLeadTime from "@awc/App/Components/Product/ProductLeadTime";
import ProductQuoteButton from "@awc/App/Components/Product/ProductQuoteButton";
import Skeleton from "@awc/App/Components/Skeleton/Skeleton";
import xUseOverlayBadge from "@awc/App/Hooks/xUseOverlayBadge";
import Badge from "@awc/MobiusOverrides/Badge/Badge";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@awc/Overrides/Widgets/ProductList/ProductListProductImage";
import { HasIsLoading } from "@awc/XDL/types";
import { mergeWithCss, sharedBadgeStyle } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";

import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// <XNG-Changes>
const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});
// </XNG-Changes>

// XNG-Change add HasIsLoading
type Props = ProductCardSelections & HasProductContext & ReturnType<typeof mapStateToProps> & HasIsLoading;

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    // XNG-Changes
    productGridContainerStyles?: ProductGridContainerStyle;
    productPriceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productListCompareAddToListWrapper?: ProductListProductImageStyles;
    badgeContainer?: InjectableCss;
    badgeAreaWrapper?: InjectableCss;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    productListProductImage?: ProductListProductImageStyles;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding-bottom: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            margin: auto;
            padding: 10px 50px;
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    productContext,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    // XNG-Changes
    productSettings,
    isLoading,
}) => {
    if (isLoading) {
        return (
            <>
                <ProductGridContainer
                    extendedStyles={styles.productGridContainerStyles}
                    id={`product_${isLoading ? "" : productContext.product.id}`}
                    data-test-selector="productContainer"
                >
                    <ProductGridContainer.Image>
                        <>
                            <Skeleton height="27px" />
                        </>
                    </ProductGridContainer.Image>
                    <ProductGridContainer.Info>
                        <Skeleton height="200px" />
                    </ProductGridContainer.Info>
                    <ProductGridContainer.Price>
                        {showPrice && (
                            <ProductGridContainer.Price>
                                <Skeleton height="27px" />
                            </ProductGridContainer.Price>
                        )}
                    </ProductGridContainer.Price>
                    <ProductGridContainer.Availability>
                        <Skeleton height="16px" />
                    </ProductGridContainer.Availability>
                    <ProductGridContainer.AddToCartActions>
                        <Skeleton height="40px" />
                    </ProductGridContainer.AddToCartActions>
                    <ProductGridContainer.AddToList>
                        {(showCompare || showAddToList) && <Skeleton height="14px" />}
                    </ProductGridContainer.AddToList>
                </ProductGridContainer>
            </>
        );
    }

    // XNG-Changes: use xUseOverlayBadge
    const { showOverlayBadge, result, badgeAreaWrapperRef, overlayBadgeWrapperClickHandler } = xUseOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        notPositioned: true,
    });

    // XNG-Changes: use ProductGridContainer
    return (
        <>
            <ProductGridContainer
                extendedStyles={styles.productGridContainerStyles}
                id={`product_${productContext.product.id}`}
                data-test-selector="productContainer"
            >
                <ProductGridContainer.Image>
                    <>
                        {productContext.product.isSponsored && (
                            <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                        )}
                        {/* XNG-Changes: Display badges here instead of as overlays, 
                            since the grid view has `showImage` set to false and 
                            they aren't technically overlaid on an image. */}
                        {showOverlayBadge && result.length && (
                            <StyledWrapper
                                {...styles.badgeAreaWrapper}
                                ref={badgeAreaWrapperRef}
                                data-test-selector="productListBadge"
                            >
                                {result.map(({ overlayBadge, overlayStyles }) => (
                                    <StyledWrapper
                                        key={overlayBadge.id}
                                        id={overlayBadge.id}
                                        {...mergeWithCss(overlayStyles, styles.badgeContainer)}
                                        onClick={overlayBadgeWrapperClickHandler}
                                    >
                                        <StyledWrapper key={overlayBadge.id} id={overlayBadge.id}>
                                            <Badge
                                                type={overlayBadge.badgeType}
                                                key={overlayBadge.id}
                                                value={
                                                    overlayBadge.badgeType === "Image"
                                                        ? overlayBadge.largeImageBadgePath
                                                        : overlayBadge.displayText
                                                }
                                                badgeTextColor={overlayBadge.textColorHexCode}
                                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                                imageAltText={
                                                    overlayBadge.imageAltText
                                                        ? overlayBadge.imageAltText
                                                        : overlayBadge.name
                                                }
                                                textBadgeCss={{
                                                    ...sharedBadgeStyle.badgeText,
                                                }}
                                                imageBadgeProps={styles.badgeImageProps}
                                                // custom style for custom badges
                                                {...{
                                                    textBadgeWrapperCss: {
                                                        ...mergeWithCss(sharedBadgeStyle.badge, {
                                                            css: css`
                                                                width: max-content;
                                                                max-width: 100%;
                                                            `,
                                                        }),
                                                    },
                                                }}
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                            </StyledWrapper>
                        )}
                        <ProductListProductImage
                            // XNG-Changes: showImage for grid view is false
                            showImage={false}
                            showCompare={false}
                            // XNG-Changes: Set showImageBadge and showTextBadge to false because they are already displayed above
                            showImageBadge={false}
                            showTextBadge={false}
                            {...{ showCustomBadge: true }}
                            extendedStyles={styles.productListProductImage}
                        />
                    </>
                </ProductGridContainer.Image>
                <ProductGridContainer.Info>
                    <ProductListProductInformation
                        showAttributes={showAttributes}
                        showAvailability={showAvailability}
                        showBrand={showBrand}
                        showPartNumbers={showPartNumbers}
                        showTitle={showTitle}
                        showImageBadges={showImageBadges}
                        maxImageBadges={maximumNumberOfImageBadges}
                        showTextBadges={showTextBadges}
                        maxTextBadges={maximumNumberOfTextBadges}
                    />
                </ProductGridContainer.Info>
                <ProductGridContainer.Price>
                    {showPrice && (
                        <ProductGridContainer.Price>
                            <ProductPrice
                                product={productContext}
                                showInvalidPriceMessage={true}
                                showPack={false}
                                showLabel={false}
                                showSavings={true}
                                showSavingsAmount={productSettings.showSavingsAmount}
                                showSavingsPercent={productSettings.showSavingsPercent}
                                extendedStyles={styles.productPriceStyles}
                                // XNG-Changes
                                showUnitOfMeasure={true}
                                {...{ includeBreakPriceRange: true }}
                            />
                            <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                        </ProductGridContainer.Price>
                    )}
                </ProductGridContainer.Price>
                <ProductGridContainer.Availability>
                    {showAvailability && <ProductLeadTime />}
                </ProductGridContainer.Availability>
                <ProductGridContainer.AddToCartActions>
                    <ProductQuoteButton />
                    <ProductBuyButton isPlp={true} />
                </ProductGridContainer.AddToCartActions>
                <ProductGridContainer.AddToList>
                    {(showCompare || showAddToList) && (
                        <ProductListProductImage
                            showImage={false}
                            showCompare={showCompare}
                            {...{ showAddToList }}
                            extendedStyles={styles.productListCompareAddToListWrapper}
                        />
                    )}
                </ProductGridContainer.AddToList>
            </ProductGridContainer>
        </>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductGridCard));
